import React, { ReactElement } from "react";
import { BiBlock } from "react-icons/bi";
import { HiOutlineChevronDoubleDown, HiOutlineChevronDoubleUp } from "react-icons/hi";
import { IoReorderTwoOutline } from "react-icons/io5";
import {
    TicketPriority,
} from "../../../__generated__/api";
import { Filters } from "../../filters/Filters";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";

export interface PriorityPickerProps<TIsMulti = false, TIsClearable=false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    isClearable?: TIsClearable;
    shouldShowDropdown?: boolean;
    className?: string;
    toggleClassName?: string;
    toggleText?: string
    value: (TIsMulti extends true ? TicketPriority[] : TicketPriority) | undefined;
    onSelected: (
        sourceId: (TIsMulti extends true ? TicketPriority[] : TicketPriority) | (TIsClearable extends true ? undefined : never)
    ) => void;
}

export const PRIORITY_TEXT = {
    [TicketPriority.Critical]: "Critical",
    [TicketPriority.High]: "High",
    [TicketPriority.Medium]: "Medium",
    [TicketPriority.Low]: "Low",
};

export const PRIORITY_ICON = {
    [TicketPriority.Critical]: (
        <div className="d-flex align-items-center">
            <BiBlock className="me-1 text-danger " />
            {" "}
            Critical
        </div>
    ),
    [TicketPriority.High]: (
        <div className="d-flex align-items-center">
            <HiOutlineChevronDoubleUp className="me-1 text-warning" />
            {" "}
            High
        </div>
    ),
    [TicketPriority.Medium]: (
        <div className="d-flex align-items-center">
            <IoReorderTwoOutline className="me-1 text-primary" />
            {" "}
            Medium
        </div>
    ),
    [TicketPriority.Low]: (
        <div className="d-flex align-items-center">
            <HiOutlineChevronDoubleDown className="me-1 text-light" />
            {" "}
            Low
        </div>
    ),

};

const Priority = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, className, shouldShowDropdown, toggleText, toggleClassName, isClearable,
}: PriorityPickerProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    type SelectedType = TIsMulti extends true ? readonly Option<string>[] : Option<string>;
    type ValueType = TIsMulti extends true ? Option<string>[] : Option<string>;

    const handleSelected = (selected: SelectedType) => {
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as TicketPriority)
            : (selected as Option<string>).value) as Parameters<typeof onSelected>[0];
        onSelected(values);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars

    const selected = Array.isArray(value)
        ? value.map(row => ({
            value: row,
            label: PRIORITY_ICON[row],
        })).filterNull()

        : {
            value,
            label: PRIORITY_ICON[value as TicketPriority],
        };
    if (shouldShowDropdown) {
        return (
            <Filters
                toggleText={toggleText}
                isMulti={isMulti}
                categories={Object.entries(PRIORITY_TEXT).map(([id, name]) => ({
                    id,
                    name,
                }))}
                className={className}
                toggleClassName={toggleClassName}
                selectedCategories={
                    (isMulti ? new Set(value) : (value as string)) as TIsMulti extends true ? Set<string> : string
                }
                onSelected={current => (isMulti
                    ? (onSelected as ((value: string[]) => void))(Array.from(current))
                    : (onSelected as ((value: string) => void))(current as string))}
            />
        );
    }
    return (
        <Select
            ref={ref}
            className={className}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={Object.entries(PRIORITY_ICON).map(([key, label]) => ({
                label,
                value: key,
            }))}
            value={selected as ValueType}
            isClearable={isClearable}

        />
    );
});

Priority.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    isClearable: undefined,
    shouldShowDropdown: undefined,
    className: undefined,
    toggleClassName: undefined,
    toggleText: undefined,
};

export default Priority as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false,
> (
    props: PriorityPickerProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof Priority>;
